/**
 * Permission Matrix Dialog - User Access Management Module
 * Created: 2024-11-12
 * Purpose: Matrix UI for managing role permissions
 * Part of: User Access Management Module Implementation - Phase 4.3
 */

import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, Checkbox, Accordion, AccordionSummary, AccordionDetails, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, CircularProgress, Alert, LinearProgress, Tooltip, TextField, InputAdornment, FormControlLabel, Switch } from '@mui/material';
import { ExpandMore as ExpandMoreIcon, CheckCircle as CheckCircleIcon, Search as SearchIcon, SelectAll as SelectAllIcon, Clear as ClearIcon, Info as InfoIcon } from '@mui/icons-material';
import { customRolesAPI, permissionsAPI } from '../../services/api';

// ============================================================================
// INTERFACES
// ============================================================================

interface Permission {
  id: number;
  resource: string;
  action: string;
  display_name: string;
  description: string;
  category: string;
  permission_string: string;
}

interface PermissionCategory {
  category: string;
  category_display_name: string;
  permissions: Permission[];
}

interface CustomRole {
  id: number;
  name: string;
  description: string | null;
  is_default: boolean;
  is_active: boolean;
  permission_count: number;
  user_count: number;
}

interface PermissionMatrixDialogProps {
  open: boolean;
  role: CustomRole;
  onClose: () => void;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const PermissionMatrixDialog: React.FC<PermissionMatrixDialogProps> = ({
  open,
  role,
  onClose
}) => {
  const [permissionsGrouped, setPermissionsGrouped] = useState<PermissionCategory[]>([]);
  const [selectedPermissions, setSelectedPermissions] = useState<Set<string>>(new Set());
  const [initialPermissions, setInitialPermissions] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
  const [showOnlySelected, setShowOnlySelected] = useState(false);

  // ============================================================================
  // FETCH DATA
  // ============================================================================

  useEffect(() => {
    if (open) {
      fetchData();
    }
  }, [open, role.id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all permissions grouped by category
      const allPermissions = await permissionsAPI.getPermissionsGrouped();
      setPermissionsGrouped(allPermissions);

      // Fetch current role permissions
      // 14/11/2025 - Fix: API returns permissions array directly, not wrapped in object
      const rolePerms = await customRolesAPI.getRolePermissions(role.id);
      const permSet = new Set<string>(rolePerms || []);
      setSelectedPermissions(permSet);
      setInitialPermissions(new Set<string>(permSet));

      // Auto-expand categories with selected permissions
      const categoriesToExpand = allPermissions
        .filter((cat: PermissionCategory) => cat.permissions.some((p: Permission) => permSet.has(p.permission_string)))
        .map((cat: PermissionCategory) => cat.category);
      setExpandedCategories(categoriesToExpand);

    } catch (err: any) {
      console.error('Error fetching permission data:', err);
      setError(err.response?.data?.detail || 'Failed to load permissions');
    } finally {
      setLoading(false);
    }
  };

  // ============================================================================
  // PERMISSION MANAGEMENT
  // ============================================================================

  const handleTogglePermission = (permissionString: string) => {
    const newSelected = new Set(selectedPermissions);
    if (newSelected.has(permissionString)) {
      newSelected.delete(permissionString);
    } else {
      newSelected.add(permissionString);
    }
    setSelectedPermissions(newSelected);
  };

  const handleToggleCategory = (category: PermissionCategory) => {
    const categoryPermissions = category.permissions.map(p => p.permission_string);
    const allSelected = categoryPermissions.every(p => selectedPermissions.has(p));

    const newSelected = new Set(selectedPermissions);
    if (allSelected) {
      // Deselect all
      categoryPermissions.forEach(p => newSelected.delete(p));
    } else {
      // Select all
      categoryPermissions.forEach(p => newSelected.add(p));
    }
    setSelectedPermissions(newSelected);
  };

  const handleSelectAll = () => {
    const allPermissions = permissionsGrouped.flatMap(cat =>
      cat.permissions.map(p => p.permission_string)
    );
    setSelectedPermissions(new Set(allPermissions));
  };

  const handleClearAll = () => {
    setSelectedPermissions(new Set());
  };

  // ============================================================================
  // SAVE CHANGES
  // ============================================================================

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      // Added: 2024-11-12 - Prevent editing default role permissions
      if (role.is_default) {
        setError('Cannot edit permissions for default system roles (SUPER_ADMIN, ADMIN, AGENT). These roles have fixed permissions.');
        setSaving(false);
        return;
      }

      await customRolesAPI.updateRolePermissions(
        role.id,
        Array.from(selectedPermissions)
      );

      onClose();
    } catch (err: any) {
      console.error('Error saving permissions:', err);
      const errorMsg = err.response?.data?.detail || 'Failed to save permissions';
      setError(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  // ============================================================================
  // FILTERING
  // ============================================================================

  const getFilteredCategories = (): PermissionCategory[] => {
    return permissionsGrouped
      .map(category => {
        const filteredPermissions = category.permissions.filter(permission => {
          const matchesSearch = searchTerm === '' ||
            permission.display_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            permission.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
            permission.permission_string.toLowerCase().includes(searchTerm.toLowerCase());

          const matchesSelectedFilter = !showOnlySelected || selectedPermissions.has(permission.permission_string);

          return matchesSearch && matchesSelectedFilter;
        });

        return {
          ...category,
          permissions: filteredPermissions
        };
      })
      .filter(category => category.permissions.length > 0);
  };

  // ============================================================================
  // UI HELPERS
  // ============================================================================

  const getCategorySelectedCount = (category: PermissionCategory): number => {
    return category.permissions.filter(p => selectedPermissions.has(p.permission_string)).length;
  };

  const hasChanges = (): boolean => {
    if (selectedPermissions.size !== initialPermissions.size) return true;

    for (const perm of selectedPermissions) {
      if (!initialPermissions.has(perm)) return true;
    }
    return false;
  };

  const handleToggleExpanded = (category: string) => {
    setExpandedCategories(prev =>
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  // ============================================================================
  // RENDER METHODS
  // ============================================================================

  const filteredCategories = getFilteredCategories();

  const renderPermissionRow = (permission: Permission) => {
    const isSelected = selectedPermissions.has(permission.permission_string);

    return (
      <TableRow key={permission.id} hover>
        <TableCell padding="checkbox">
          <Checkbox
            checked={isSelected}
            onChange={() => handleTogglePermission(permission.permission_string)}
            disabled={role.is_default}
          />
        </TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight={500}>
            {permission.display_name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {permission.permission_string}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" color="text.secondary">
            {permission.description}
          </Typography>
        </TableCell>
      </TableRow>
    );
  };

  const renderCategory = (category: PermissionCategory) => {
    const selectedCount = getCategorySelectedCount(category);
    const totalCount = category.permissions.length;
    const allSelected = selectedCount === totalCount;
    const someSelected = selectedCount > 0 && selectedCount < totalCount;
    const isExpanded = expandedCategories.includes(category.category);

    return (
      <Accordion
        key={category.category}
        expanded={isExpanded}
        onChange={() => handleToggleExpanded(category.category)}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center" width="100%">
            <Checkbox
              checked={allSelected}
              indeterminate={someSelected}
              onChange={(e) => {
                e.stopPropagation();
                handleToggleCategory(category);
              }}
              onClick={(e) => e.stopPropagation()}
              disabled={role.is_default}
            />
            <Typography variant="subtitle1" fontWeight={600} sx={{ flexGrow: 1 }}>
              {category.category_display_name}
            </Typography>
            <Chip
              label={`${selectedCount}/${totalCount}`}
              size="small"
              color={selectedCount > 0 ? 'primary' : 'default'}
              sx={{ mr: 2 }}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>Permission</TableCell>
                  <TableCell>Description</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {category.permissions.map(permission => renderPermissionRow(permission))}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>
    );
  };

  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '90vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6">
              Manage Permissions: {role.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {role.description || 'Configure role permissions'}
            </Typography>
          </Box>
          <Chip
            label={`${selectedPermissions.size} permissions`}
            color="primary"
            icon={<CheckCircleIcon />}
          />
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <>
            {/* Toolbar */}
            <Box mb={2}>
              <Box display="flex" gap={2} mb={2} flexWrap="wrap">
                <TextField
                  placeholder="Search permissions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  size="small"
                  sx={{ flexGrow: 1, minWidth: '200px' }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    )
                  }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={showOnlySelected}
                      onChange={(e) => setShowOnlySelected(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Show only selected"
                />
                <Tooltip title="Select All">
                  <Button
                    size="small"
                    startIcon={<SelectAllIcon />}
                    onClick={handleSelectAll}
                    disabled={role.is_default}
                  >
                    All
                  </Button>
                </Tooltip>
                <Tooltip title="Clear All">
                  <Button
                    size="small"
                    startIcon={<ClearIcon />}
                    onClick={handleClearAll}
                    disabled={role.is_default}
                  >
                    None
                  </Button>
                </Tooltip>
              </Box>

              {/* Info Alert - Updated: 2024-11-12 - Clarify default roles are read-only */}
              {role.is_default && (
                <Alert severity="warning" icon={<InfoIcon />} sx={{ mb: 2 }}>
                  <strong>Read-Only:</strong> Default system roles (SUPER_ADMIN, ADMIN, AGENT) have fixed permissions that cannot be modified. You can view permissions here but cannot save changes. Create a custom role to define specific permissions.
                </Alert>
              )}

              {/* Changes Alert */}
              {hasChanges() && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  You have unsaved changes
                </Alert>
              )}
            </Box>

            {/* Permission Categories */}
            {filteredCategories.length === 0 ? (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                minHeight="300px"
              >
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No permissions found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Try adjusting your search or filters
                </Typography>
              </Box>
            ) : (
              filteredCategories.map(category => renderCategory(category))
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Box display="flex" justifyContent="space-between" width="100%" px={2}>
          <Typography variant="body2" color="text.secondary">
            {selectedPermissions.size} of {permissionsGrouped.flatMap(c => c.permissions).length} permissions selected
          </Typography>
          <Box>
            <Button onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={saving || !hasChanges() || role.is_default}
              sx={{ ml: 1 }}
            >
              {saving ? 'Saving...' : role.is_default ? 'Read-Only' : 'Save Changes'}
            </Button>
          </Box>
        </Box>
      </DialogActions>

      {saving && <LinearProgress />}
    </Dialog>
  );
};

export default PermissionMatrixDialog;
